import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { toast } from 'sonner'
import { AlertCircle, Copy, Package, RefreshCw, Trash2 } from 'lucide-react'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'

type Repository = {
  id: number
  name: string
  full_name: string
  visibility: 'public_visibility' | 'private_visibility'
  created_at: string
}

type Tag = {
  name: string
  size: number | null
  createdAt: string | null
  digest: string | null
}

type Props = {
  repository: Repository
  registryUrl: string
  tags: Tag[]
  tagsError: string | null
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '-'
  if (bytes === 0) return '0 B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-'

  try {
    const date = new Date(dateString)
    return format(date, 'yyyy/MM/dd HH:mm', { locale: ja })
  } catch {
    return '-'
  }
}

export default function RepositoriesShow({ repository, registryUrl, tags, tagsError }: Props) {
  const [deleteRepoDialogOpen, setDeleteRepoDialogOpen] = useState(false)
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)

  const imageName = `${registryUrl}/${repository.full_name}`

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('コピーしました')
  }

  const handleDeleteRepository = () => {
    router.delete(`/repositories/${repository.id}`, {
      onSuccess: () => {
        toast.success('リポジトリを削除しました')
      },
    })
  }

  const handleSyncTags = () => {
    setIsSyncing(true)
    toast.loading('タグを同期中...', { id: 'sync-tags' })

    router.post(`/repositories/${repository.id}/sync_tags`, {}, {
      onFinish: () => {
        setIsSyncing(false)
        toast.dismiss('sync-tags')
      },
    })
  }

  const handleDeleteTag = () => {
    if (!tagToDelete?.digest) return

    setIsDeleting(true)
    toast.loading('タグを削除中...', { id: 'delete-tag' })

    // window.location.href で遷移
    window.location.href = `/repositories/${repository.id}/delete_tag?digest=${encodeURIComponent(tagToDelete.digest)}`
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{repository.full_name}</h1>
            <Badge
              variant={repository.visibility === 'public_visibility' ? 'default' : 'secondary'}
            >
              {repository.visibility === 'public_visibility' ? 'Public' : 'Private'}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncTags}
              disabled={isSyncing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              同期
            </Button>
            <Dialog open={deleteRepoDialogOpen} onOpenChange={setDeleteRepoDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  <Trash2 className="w-4 h-4 mr-2" />
                  削除
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>リポジトリを削除しますか？</DialogTitle>
                <DialogDescription>
                  この操作は取り消せません。リポジトリ内のすべてのイメージが削除されます。
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDeleteRepoDialogOpen(false)}>
                  キャンセル
                </Button>
                <Button variant="destructive" onClick={handleDeleteRepository}>
                  削除する
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>使い方</CardTitle>
            <CardDescription>
              Docker CLI を使用してイメージを push/pull します
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-medium mb-2">1. ログイン</h3>
              <CommandBlock
                command={`docker login ${registryUrl}`}
                onCopy={() => copyToClipboard(`docker login ${registryUrl}`)}
              />
            </div>

            <div>
              <h3 className="font-medium mb-2">2. イメージをタグ付け</h3>
              <CommandBlock
                command={`docker tag <image> ${imageName}:<tag>`}
                onCopy={() => copyToClipboard(`docker tag <image> ${imageName}:<tag>`)}
              />
            </div>

            <div>
              <h3 className="font-medium mb-2">3. Push</h3>
              <CommandBlock
                command={`docker push ${imageName}:<tag>`}
                onCopy={() => copyToClipboard(`docker push ${imageName}:<tag>`)}
              />
            </div>

            <div>
              <h3 className="font-medium mb-2">Pull</h3>
              <CommandBlock
                command={`docker pull ${imageName}:<tag>`}
                onCopy={() => copyToClipboard(`docker pull ${imageName}:<tag>`)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>タグ一覧</CardTitle>
            <CardDescription>
              このリポジトリにpushされたイメージタグ
            </CardDescription>
          </CardHeader>
          <CardContent>
            {tagsError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{tagsError}</AlertDescription>
              </Alert>
            ) : tags.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mb-4" />
                <p>まだイメージがpushされていません</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>タグ</TableHead>
                    <TableHead>ダイジェスト</TableHead>
                    <TableHead>サイズ</TableHead>
                    <TableHead>作成日時</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tags.map((tag) => (
                    <TableRow key={tag.name}>
                      <TableCell>
                        <code className="text-sm font-mono">{tag.name}</code>
                      </TableCell>
                      <TableCell>
                        {tag.digest && (
                          <code
                            className="text-xs font-mono text-muted-foreground cursor-pointer hover:text-foreground"
                            onClick={() => copyToClipboard(tag.digest!)}
                            title={tag.digest}
                          >
                            {tag.digest.replace('sha256:', '').slice(0, 12)}
                          </code>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatBytes(tag.size)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDate(tag.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(`${imageName}:${tag.name}`)}
                            title="イメージ名をコピー"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setTagToDelete(tag)}
                            title="タグを削除"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* タグ削除確認ダイアログ */}
        <Dialog open={!!tagToDelete} onOpenChange={(open) => !open && setTagToDelete(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>タグを削除しますか？</DialogTitle>
              <DialogDescription>
                タグ「{tagToDelete?.name}」を削除します。この操作は取り消せません。
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTagToDelete(null)} disabled={isDeleting}>
                キャンセル
              </Button>
              <Button variant="destructive" onClick={handleDeleteTag} disabled={isDeleting}>
                {isDeleting ? '削除中...' : '削除する'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  )
}

function CommandBlock({ command, onCopy }: { command: string; onCopy: () => void }) {
  return (
    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
      <code className="flex-1 text-sm font-mono">{command}</code>
      <Button variant="ghost" size="sm" onClick={onCopy}>
        <Copy className="w-4 h-4" />
      </Button>
    </div>
  )
}
